import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '@csx/security';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthenticatedUser } from '../model/authenticated-user.model';
import { MessageDetails } from '../model/message-details.model';
import { UserProfile } from '../model/user-profile.model';
import { AppUtilityService } from './app-utility.service';
import { ConfigService } from './config.service';
import { LmsLoggerService } from './lms-logger.service';
import { MessageHandlerService } from './message-handler.service';
import { StaticDataStoreService } from './static-datastore.service';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../../../environments/environment';


/**
 * @export
 * @class UserService
 */
/**
 * Interface for RUM user data matching the proven pattern
 */
interface RumUserData {
    id: string;
    name?: string;
    email?: string;
    department?: string;
    language?: string;
    roles?: string[];
    employeeType?: string; 
    workAddress?: string;
    workCity?: string;
    workPostalCode?: string;
    workState?: string;
    [key: string]: any; // Index signature for Datadog RUM compatibility
}

@Injectable()
export class UserService {
    readonly managerOktaRole =  "client.lms.manager";
    readonly directorOktaRole = "client.lms.director";

    userInfo: Observable<AuthenticatedUser>;
    private userInfoBS: BehaviorSubject<AuthenticatedUser>;
    private RESOURCE_URL = '/user/authdata';
    configData: any;

    /**
     * Creates an instance of UserService.
     * @param {HttpClient} http
     * @param {ConfigService} configService
     * @param {StaticDataStoreService} staticDataStoreService
     * @param {LmsLoggerService} lmsLoggerService
     * @param {MessageHandlerService} messageHandlerService
     * @param {Router} router
     *
     * @memberOf UserService
     */
    constructor(private http: HttpClient,
        private configService: ConfigService,
        private staticDataStoreService: StaticDataStoreService,
        private lmsLoggerService: LmsLoggerService,
        private messageHandlerService: MessageHandlerService,
        private appUtilityService: AppUtilityService,
        private oidcUser: User) {
        this.configData = this.configService.getConfigData();
        this.userInfoBS = <BehaviorSubject<AuthenticatedUser>>new BehaviorSubject({});
        this.userInfo = this.userInfoBS.asObservable();
    }

    public get isUserManager() {
        return this.oidcUser && this.oidcUser.isRoleAuthorized([this.managerOktaRole]);
    }
    public get isUserDirector() {
        return this.oidcUser && this.oidcUser.isRoleAuthorized([this.directorOktaRole]);
    }
    public get isUserManagerOrDirector() {
        return this.isUserManager || this.isUserDirector;
    }
    
    /**
     * This is used to Load the User informations
     * @memberOf UserService
     */
    userDataAlreadyExist() {
        const userInfo = this.staticDataStoreService.getValue('user');
        if (userInfo) {
            this.userInfo = userInfo;
            return true;
        }
        return false;
    }

    /**
     * @param {any} response
     *
     * @memberOf UserService
     */
    load(response) {
        const user = <AuthenticatedUser>(response);
        this.staticDataStoreService.putValue('user', user);
        if (user.userPreference) {
            const userProfile = <UserProfile>(user.userPreference);
            this.staticDataStoreService.putValue('userProfile', userProfile);
        }
        this.userInfoBS.next(user);
        this.lmsLoggerService.logInfo('UserService => load()', `User Data Fetch Successfull for ${JSON.stringify(user)}`);
        
        // Set user data for RUM monitoring after user data is loaded
        this.setRumUserData();
    }

    /**
     * @param {any} error
     *
     * @memberOf UserService
     */
    handleLoadError(error) {
        this.messageHandlerService.handleMessage(<MessageDetails>({
            type: this.configService.getConfigData().message.type.server,
            level: this.configService.getConfigData().message.level.error,
            target: this.configService.getConfigData().message.target.page,
            message: this.appUtilityService.getHttpErroMessage(error, false)
        }));
        this.userInfoBS.error(error);
    }

    /**
     * @returns
     *
     * @memberOf UserService
     */
    getData() {
        this.lmsLoggerService.logInfo('UserService=>getData service call starts');
           return this.http.get(`${this.configService.getConfigData().urls.API_URL}${this.RESOURCE_URL}`).pipe(
             tap(() => this.lmsLoggerService.logInfo('UserService=>getData service call ends'))
           );
    }

    /**
     * Initialize Datadog RUM user context
     * @memberOf UserService
     */
    private initDataDogAdditionalInfo(): void {
        try {
            // Initialize Datadog RUM user context if enabled
            if (environment.appConfig.enableDatadogRum && datadogRum) {
                const authenticatedUser: AuthenticatedUser = this.staticDataStoreService.getValue('user');
                const userProfile: UserProfile = this.staticDataStoreService.getValue('userProfile');

                if (!authenticatedUser) {
                    this.lmsLoggerService.logError('UserService => initDataDogAdditionalInfo()', 'No authenticated user found');
                    return;
                }

                // Format name as "Last Name, First Name"
                const firstName = authenticatedUser.firstName || '';
                const lastName = authenticatedUser.lastName || '';
                const formattedName = lastName && firstName ? `${lastName}, ${firstName}` : `${lastName}${firstName}`.trim();

                const rumUserData = {
                    name: formattedName || 'Unknown User',
                    id: authenticatedUser.racfId || 'unknown',
                    email: (this.oidcUser as any)?.email || '', // Get email from OIDC user
                    department: userProfile?.alertDesk || '', // Use alertDesk as department
                    language: (this.oidcUser as any)?.language || 'en', // Default to 'en' if not available
                    employeeType: authenticatedUser.accessLevel || '', // Use accessLevel as employee type
                    workAddress: (this.oidcUser as any)?.workAddress || '',
                    workCity: (this.oidcUser as any)?.workCity || '',
                    workPostalCode: (this.oidcUser as any)?.workPostalCode || '',
                    workState: (this.oidcUser as any)?.workState || ''
                };

                datadogRum.setUser(rumUserData);
                this.lmsLoggerService.logInfo('UserService => initDataDogAdditionalInfo()', 'Datadog RUM user context set successfully');
            }
        } catch (error) {
            this.lmsLoggerService.logError('UserService => initDataDogAdditionalInfo()', `Error setting Datadog RUM user context: ${error}`);
        }
    }

    /**
     * Set user data for RUM monitoring
     * @memberOf UserService
     */
    public setRumUserData(): void {
        this.initDataDogAdditionalInfo();
    }
}
